import {
	type LightningReportErrorEllipseConfidence,
	type LightningReportEventsMetadata,
	lightningStrikeDamagePotentialFields,
} from '@xweather/protect-types'
import type { LightningReportGeojson, LightningReportGeojsonFeature, ListedLightningReport } from '../../../../data/reports/lightning.ts'

// It could make sense to lower this to eg 100k, to account that users might not have a very high-end machines
export const MAX_EVENT_COUNT_TO_ALLOW_DISPLAYING_REPORT = 250_000
export const MAX_EVENTS_TO_ALWAYS_DISPLAY_ERROR_ELLIPSES = 2500

export type StrikeDamagePotentialOption = (typeof strikeDamagePotentialOptions)[number]
export const strikeDamagePotentialOptions = lightningStrikeDamagePotentialFields

type LightningReportGeojsonFeatureWithErrorEllipses = LightningReportGeojsonFeature & { properties: { ellAngleDeg: number } }
export const hasFeatureErrorEllipses = (
	feature: LightningReportGeojsonFeature,
): feature is LightningReportGeojsonFeatureWithErrorEllipses => feature.properties.ellAngleDeg != null

export const hasReportErrorEllipses = (report: ListedLightningReport) =>
	report.fields.includes('analysis') || report.fields.includes('research')
export const hasReportStrikeDamagePotential = (report: ListedLightningReport) => report.fields.includes('ldi_sdp')
export const hasReportMultipleAssets = (report: ListedLightningReport) => report.assetVrns.length > 1
export const getReportEventCount = (events: LightningReportEventsMetadata | undefined): number =>
	(events?.cloudToCloud ?? 0) + (events?.positiveCloudToGround ?? 0) + (events?.negativeCloudToGround ?? 0)

export const shouldShowAllErrorEllipsesForReport = (report: ListedLightningReport) =>
	hasReportErrorEllipses(report) && getReportEventCount(report.events) <= MAX_EVENTS_TO_ALWAYS_DISPLAY_ERROR_ELLIPSES

export const BASE_ERROR_ELLIPSE_CONFIDENCE: LightningReportErrorEllipseConfidence = 50

export const errorEllipseConfidenceToMultiplier = (confidence: LightningReportErrorEllipseConfidence) => {
	switch (confidence) {
		case 50:
			return 1
		case 80:
			return 1.52
		case 90:
			return 1.82
		case 99:
			return 2.57
		default:
			confidence satisfies never
			console.warn('Unrecognized error ellipse confidence', confidence)
			return 1
	}
}

export const signalStrengthRangeFromReport = (
	report: ListedLightningReport | undefined,
	geojson: LightningReportGeojson | undefined,
): { min: number; max: number } | undefined => {
	const range = (() => {
		if (report?.events?.minSignalStrengthKA != null) {
			return { min: report.events.minSignalStrengthKA, max: report.events.maxSignalStrengthKA }
		}

		if (!geojson || geojson.features.length === 0) {
			return undefined
		}

		let min = Number.POSITIVE_INFINITY
		let max = Number.NEGATIVE_INFINITY
		for (const feature of geojson.features) {
			min = Math.min(min, feature.properties.signalStrengthKA)
			max = Math.max(max, feature.properties.signalStrengthKA)
		}

		return { min, max }
	})()

	if (!range) return undefined
	// Push the bounds strictly past every event: floor/ceil alone leaves an
	// integer-valued min/max sitting exactly on the slider end
	return {
		min: Number.isInteger(range.min) ? range.min - 1 : Math.floor(range.min),
		max: Number.isInteger(range.max) ? range.max + 1 : Math.ceil(range.max),
	}
}
